import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Plus, Minus, X, Eye } from 'lucide-react';
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths,
  format, isSameMonth,
} from 'date-fns';
import { id as localeId } from 'date-fns/locale';
import api, { apiError } from '../lib/api';
import { formatDate, formatIDR, todayStr, shiftDateStr } from '../lib/format';
import { TombolWa } from '../components/KirimWa';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const inputCls = 'w-full rounded-xl border border-input bg-ivory/50 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30';
const labelCls = 'text-xs font-semibold uppercase tracking-wider text-mocha';

function BookingForm({ onSaved }) {
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [useDate, setUseDate] = useState(shiftDateStr(todayStr(), 1));
  const [availability, setAvailability] = useState([]);
  const [picks, setPicks] = useState([]);
  const [productSelect, setProductSelect] = useState('');
  const [event, setEvent] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const pickup = shiftDateStr(useDate, -1);
  const ready = shiftDateStr(useDate, 2);

  useEffect(() => {
    api.get('/customers').then((r) => setCustomers(r.data));
  }, []);

  useEffect(() => {
    if (!useDate) return;
    api.get(`/products-availability?start=${pickup}&end=${ready}`).then((r) => setAvailability(r.data));
  }, [useDate, pickup, ready]);

  const addProduct = (pid) => {
    const p = availability.find((x) => x.id === pid);
    if (!p) return;
    if (picks.find((x) => x.product_id === pid)) return;
    setPicks([...picks, { product_id: pid, sizes: {} }]);
    setProductSelect('');
  };

  const submit = async () => {
    if (!customerId) return toast.error('Pilih customer');
    const rows = picks
      .map((p) => ({ ...p, sizes: Object.fromEntries(Object.entries(p.sizes).filter(([, q]) => q > 0)) }))
      .filter((p) => Object.keys(p.sizes).length > 0);
    if (rows.length === 0) return toast.error('Pilih barang dan jumlahnya');
    setSaving(true);
    try {
      await api.post('/bookings', { customer_id: customerId, use_date: useDate, rows, event, notes });
      toast.success('Booking tersimpan');
      onSaved();
    } catch (err) {
      toast.error(apiError(err, 'Gagal menyimpan booking'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className={labelCls}>Customer</label>
        <select data-testid="booking-customer-select" value={customerId} onChange={(e) => setCustomerId(e.target.value)} className={`${inputCls} mt-1.5`}>
          <option value="">Pilih customer…</option>
          {customers.map((c) => (
            <option key={c.id} value={c.id}>{`${c.name} — ${c.whatsapp || 'tanpa WA'}`}</option>
          ))}
        </select>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className={labelCls}>Tgl Pakai</label>
          <input data-testid="booking-use-date" type="date" value={useDate} onChange={(e) => { setUseDate(e.target.value); setPicks([]); }} className={`${inputCls} mt-1.5`} />
        </div>
        <div>
          <label className={labelCls}>Ambil (H-1)</label>
          <p data-testid="booking-pickup-auto" className="mt-1.5 rounded-xl bg-brown/5 border border-brown/15 px-3 py-2.5 text-sm font-bold text-brown">{pickup}</p>
        </div>
        <div>
          <label className={labelCls}>Kembali (H+1)</label>
          <p data-testid="booking-return-auto" className="mt-1.5 rounded-xl bg-brown/5 border border-brown/15 px-3 py-2.5 text-sm font-bold text-brown">{shiftDateStr(useDate, 1)}</p>
        </div>
      </div>
      <div>
        <label className={labelCls}>Tambah Barang (stok otomatis cek bentrok)</label>
        <select data-testid="booking-product-select" value={productSelect} onChange={(e) => addProduct(e.target.value)} className={`${inputCls} mt-1.5`}>
          <option value="">Pilih barang…</option>
          {availability.filter((p) => p.available_total > 0 && !picks.find((x) => x.product_id === p.id)).map((p) => (
            <option key={p.id} value={p.id}>{`${p.code} — ${p.name} (ready ${p.available_total})`}</option>
          ))}
        </select>
      </div>
      <div className="space-y-2" data-testid="booking-items-container">
        {picks.map((pick, i) => {
          const p = availability.find((x) => x.id === pick.product_id);
          if (!p) return null;
          const sizeKeys = Object.keys(p.sizes).filter((s) => p.sizes[s] > 0);
          return (
            <div key={pick.product_id} data-testid={`booking-pick-${p.code}`} className="rounded-xl border border-border bg-white p-3.5">
              <div className="flex items-center justify-between">
                <p className="text-sm font-bold"><span className="text-brown">{p.code}</span> {p.name}</p>
                <button type="button" data-testid={`booking-pick-remove-${p.code}`} onClick={() => setPicks(picks.filter((_, j) => j !== i))} className="p-1 text-danger hover:bg-danger/10 rounded-lg">
                  <X size={14} />
                </button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {sizeKeys.map((s) => {
                  const avail = p.available_sizes?.[s] ?? 0;
                  const val = pick.sizes[s] || 0;
                  return (
                    <div key={s} className={`rounded-xl border px-2.5 py-1.5 text-center ${avail === 0 ? 'opacity-40 border-border' : 'border-border bg-ivory/50'}`}>
                      <p className="text-[9px] font-bold text-mocha">{s} • stok {avail}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <button type="button" data-testid={`booking-size-${p.code}-${s.replace(/[^A-Z0-9]/gi, '')}-minus`} disabled={val <= 0} onClick={() => setPicks(picks.map((x, j) => j === i ? { ...x, sizes: { ...x.sizes, [s]: Math.max(0, val - 1) } } : x))} className="w-6 h-6 rounded-md border border-border bg-white text-brown flex items-center justify-center disabled:opacity-30">
                          <Minus size={11} />
                        </button>
                        <span className="w-5 text-center text-sm font-bold">{val}</span>
                        <button type="button" data-testid={`booking-size-${p.code}-${s.replace(/[^A-Z0-9]/gi, '')}-plus`} disabled={val >= avail} onClick={() => setPicks(picks.map((x, j) => j === i ? { ...x, sizes: { ...x.sizes, [s]: Math.min(avail, val + 1) } } : x))} className="w-6 h-6 rounded-md border border-border bg-white text-brown flex items-center justify-center disabled:opacity-30">
                          <Plus size={11} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
        {picks.length === 0 && <p className="text-xs text-mocha">Belum ada barang dipilih.</p>}
      </div>
      <input data-testid="booking-event-input" value={event} onChange={(e) => setEvent(e.target.value)} placeholder="Acara (wisuda, akad…) — opsional" className={inputCls} />
      <textarea data-testid="booking-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Catatan (opsional)…" rows={2} className={inputCls} />
      <button data-testid="save-booking-button" onClick={submit} disabled={saving} className="w-full rounded-xl bg-brown text-white py-3 font-semibold text-sm hover:bg-brown-med transition-colors disabled:opacity-60">
        {saving ? 'Menyimpan…' : 'Simpan Booking'}
      </button>
    </div>
  );
}

export default function Bookings() {
  const [bookings, setBookings] = useState([]);
  const [events, setEvents] = useState([]);
  const [month, setMonth] = useState(new Date());
  const [selected, setSelected] = useState(todayStr());
  const [open, setOpen] = useState(false);

  const load = () => api.get('/bookings').then((r) => setBookings(r.data));
  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const ms = format(month, 'yyyy-MM');
    api.get(`/calendar?month=${ms}`)
      .then((r) => setEvents(r.data.events))
      .catch(() => setEvents([]));
    setSelected((prev) => {
      if (prev.startsWith(ms)) return prev;
      const t = todayStr();
      return t.startsWith(ms) ? t : `${ms}-01`;
    });
  }, [month]);

  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(month), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(month), { weekStartsOn: 1 });
    const out = [];
    let cur = start;
    while (cur <= end) {
      out.push(cur);
      cur = addDays(cur, 1);
    }
    return out;
  }, [month]);

  const eventsForDay = (day) => {
    const ds = format(day, 'yyyy-MM-dd');
    return events.filter((e) => e.date === ds);
  };
  const selectedEvents = events.filter((e) => e.date === selected);
  const selectedPickups = selectedEvents.filter((e) => e.type === 'pickup');
  const selectedReturns = selectedEvents.filter((e) => e.type === 'return');

  const confirmed = bookings.filter((b) => b.status === 'confirmed');
  const upcoming = confirmed.filter((b) => b.return_date >= todayStr()).sort((a, b) => a.use_date.localeCompare(b.use_date));

  const cancelBooking = async (b) => {
    try {
      await api.post(`/bookings/${b.id}/cancel`);
      toast.success('Booking dibatalkan');
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="space-y-5" data-testid="bookings-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-3xl text-ink tracking-tight">Booking</h1>
        <button
          data-testid="new-booking-button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-2 rounded-xl bg-brown text-white px-5 py-2.5 text-sm font-semibold hover:bg-brown-med transition-colors"
        >
          <Plus size={16} /> Booking Baru
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-border shadow-soft p-4 md:p-5" data-testid="booking-calendar">
        <div className="flex items-center justify-between mb-4">
          <button data-testid="calendar-prev" onClick={() => setMonth(addMonths(month, -1))} className="p-2 rounded-xl hover:bg-ivory text-brown">
            <ChevronLeft size={18} />
          </button>
          <p className="font-display text-lg text-ink capitalize" data-testid="calendar-month-label">
            {format(month, 'MMMM yyyy', { locale: localeId })}
          </p>
          <button data-testid="calendar-next" onClick={() => setMonth(addMonths(month, 1))} className="p-2 rounded-xl hover:bg-ivory text-brown">
            <ChevronRight size={18} />
          </button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-bold uppercase tracking-wider text-mocha mb-1">
          {['Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab', 'Min'].map((d) => (
            <div key={d} className="py-1">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {days.map((day) => {
            const ds = format(day, 'yyyy-MM-dd');
            const dayEvents = eventsForDay(day);
            const pickups = dayEvents.filter((e) => e.type === 'pickup');
            const returns = dayEvents.filter((e) => e.type === 'return');
            const shown = Math.min(pickups.length, 2) + Math.min(returns.length, 2);
            const hidden = dayEvents.length - shown;
            const isToday = ds === todayStr();
            const isSelected = ds === selected;
            return (
              <button
                key={ds}
                type="button"
                onClick={() => setSelected(ds)}
                data-testid={`calendar-day-${ds}`}
                className={`min-h-[64px] md:min-h-[80px] rounded-lg p-1.5 border text-left cursor-pointer transition-colors ${
                  isSameMonth(day, month) ? 'bg-ivory/60 border-border hover:border-brown/40' : 'bg-transparent border-transparent opacity-40'
                } ${isToday ? 'ring-2 ring-brown' : ''} ${isSelected ? 'bg-caramel/20 border-caramel' : ''}`}
              >
                <p className={`text-[11px] font-bold ${isToday ? 'text-brown' : 'text-ink'}`}>{format(day, 'd')}</p>
                <div className="space-y-0.5 mt-0.5">
                  {pickups.slice(0, 2).map((ev, i) => (
                    <p key={`p${i}`} title={`Ambil: ${ev.customer_name}`} className="truncate rounded bg-gold/40 text-brown-dark text-[9px] font-bold px-1.5 py-0.5">
                      Ambil: {ev.customer_name}
                    </p>
                  ))}
                  {returns.slice(0, 2).map((ev, i) => (
                    <p key={`r${i}`} title={`Kembali: ${ev.customer_name}`} className="truncate rounded bg-success/15 text-success text-[9px] font-bold px-1.5 py-0.5">
                      Kembali: {ev.customer_name}
                    </p>
                  ))}
                  {hidden > 0 && <p className="text-[9px] text-mocha px-1">+{hidden} lagi</p>}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-border shadow-soft p-4 md:p-5" data-testid="calendar-day-detail">
        <h2 className="font-display text-xl text-ink mb-1">Jadwal {formatDate(selected)}</h2>
        <p className="text-xs text-mocha mb-4">{selectedEvents.length} agenda • klik tanggal lain di kalender untuk pindah hari</p>
        {selectedEvents.length === 0 ? (
          <p className="text-sm text-mocha" data-testid="day-detail-empty">Tidak ada pengambilan/pengembalian pada tanggal ini.</p>
        ) : (
          <div className="space-y-4">
            {selectedPickups.length > 0 && (
              <div>
                <p className="text-[11px] uppercase tracking-wider text-gold-dark font-bold mb-2">📦 Pengambilan Barang ({selectedPickups.length})</p>
                <div className="space-y-2">
                  {selectedPickups.map((ev, i) => (
                    <div key={`p${i}`} data-testid={`day-pickup-${ev.ref}`} className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-gold/10 border border-gold/30 px-4 py-3">
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-ink">{ev.customer_name} <span className="text-brown">{ev.ref}</span></p>
                        <p className="text-xs text-mocha truncate">{ev.products} • {ev.pcs} pcs</p>
                      </div>
                      <div className="flex gap-1.5 shrink-0">
                        {ev.kind === 'order' && (
                          <Link to={`/transaksi/${ev.id}`} data-testid={`day-pickup-view-${ev.ref}`} className="p-2 rounded-xl bg-brown/10 text-brown" title="Lihat order">
                            <Eye size={15} />
                          </Link>
                        )}
                        <TombolWa target={{ kind: ev.kind, id: ev.id, jenis: 'ingat_ambil' }}
                          testid={`day-pickup-wa-${ev.ref}`}
                          className="p-2 rounded-xl bg-[#25D366]/10 text-[#1da851]" title="Kirim pengingat pengambilan via WhatsApp" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {selectedReturns.length > 0 && (
              <div>
                <p className="text-[11px] uppercase tracking-wider text-success font-bold mb-2">🔄 Pengembalian Barang ({selectedReturns.length})</p>
                <div className="space-y-2">
                  {selectedReturns.map((ev, i) => (
                    <div key={`r${i}`} data-testid={`day-return-${ev.ref}`} className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-success/5 border border-success/30 px-4 py-3">
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-ink">{ev.customer_name} <span className="text-brown">{ev.ref}</span></p>
                        <p className="text-xs text-mocha truncate">
                          {ev.products} • {ev.pcs} pcs
                          {ev.kind === 'order' && ev.remaining > 0 && <span className="text-danger font-bold"> • sisa {formatIDR(ev.remaining)}</span>}
                        </p>
                      </div>
                      <div className="flex gap-1.5 shrink-0">
                        {ev.kind === 'order' && (
                          <Link to={`/transaksi/${ev.id}`} data-testid={`day-return-view-${ev.ref}`} className="p-2 rounded-xl bg-brown/10 text-brown" title="Lihat order">
                            <Eye size={15} />
                          </Link>
                        )}
                        <TombolWa target={{ kind: ev.kind, id: ev.id, jenis: 'ingat_kembali' }}
                          testid={`day-return-wa-${ev.ref}`}
                          className="p-2 rounded-xl bg-[#25D366]/10 text-[#1da851]" title="Kirim pengingat pengembalian via WhatsApp" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-border shadow-soft p-5" data-testid="upcoming-bookings-list">
        <h2 className="font-display text-xl text-ink mb-4">Booking Mendatang</h2>
        {upcoming.length === 0 ? (
          <p className="text-sm text-mocha">Belum ada booking mendatang.</p>
        ) : (
          <div className="space-y-3">
            {upcoming.map((b) => (
              <div key={b.id} data-testid={`booking-row-${b.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-ivory px-4 py-3">
                <div>
                  <p className="text-sm font-bold text-ink">
                    {b.customer_name}
                    {b.order_ref && <span className="text-mocha font-normal text-xs"> • #{b.order_ref}</span>}
                  </p>
                  <p className="text-xs text-mocha">
                    Pakai {formatDate(b.use_date)} • ambil {formatDate(b.pickup_date)} → kembali {formatDate(b.return_date)} • {b.pcs} pcs
                    {b.notes ? ` • ${b.notes}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex gap-1.5 flex-wrap">
                    {b.rows.slice(0, 3).map((r, i) => (
                      <span key={i} className="rounded-full bg-brown/10 text-brown text-[10px] font-bold px-2.5 py-0.5">
                        {r.product_code} ×{r.qty}
                      </span>
                    ))}
                  </div>
                  <button data-testid={`cancel-booking-${b.id}`} onClick={() => cancelBooking(b)} className="p-1.5 rounded-xl text-danger hover:bg-danger/10 transition-colors">
                    <X size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="booking-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-brown-dark text-xl">Booking Baru</DialogTitle>
          </DialogHeader>
          <BookingForm
            onSaved={() => {
              setOpen(false);
              load();
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
